import {
  MinutesAnonymization,
  MinutesAttribution,
  MinutesDateFormat,
  MinutesLanguage,
  MinutesNameFormat,
  MinutesPassive,
  MinutesTimeFormat,
  MinutesVerboseness,
  MinutingAgent,
} from './meetings.service'

/** The key source used to crypto-shred a meeting's artifacts. */
export type EncryptionKeySource = 'KMS'

/** Settings the client owns. */
export type ClientSettings = {
  minutesVerboseness: MinutesVerboseness
  minutesAnonymization: MinutesAnonymization
  minutesPassive: MinutesPassive
  minutesNameFormat: MinutesNameFormat
  minutesDateFormat: MinutesDateFormat
  minutesTimeFormat: MinutesTimeFormat
  minutesLanguage: MinutesLanguage
  minutesAttribution: MinutesAttribution
  anonymiseMemberSpeakers: boolean
  useTranscriptRefinement: boolean
  shareUrlOptOut: boolean
  transcriberInputLanguage: string
  transcriberOutputLanguage: string
  allowTranscriptDownload: boolean
  allowVideoDownload: boolean
  allowMinutesDownload: boolean
  allowOutlineDownload: boolean
  allowManualMeetingCreation: boolean
  allowMeetingUpload: boolean
  useVerificationDiscovery: boolean
  allowAdminMeetingActions: boolean
  allowRetentionExtensionRequests: boolean
  softDeletionPeriodInDays: number
  isIntercomEnabled: boolean
  isSharingEnabled: boolean
  isCalendarSynchronizationEnabled: boolean
  recordingType: 'VIDEO' | 'AUDIO'
  sendSoftDeletionWarningEmail: boolean
  softDeletionWarningHours: number
  sendSoftDeletionConfirmationEmail: boolean
  sendHardDeletionConfirmationEmail: boolean
  sendAssistantAccessDeniedEmail: boolean
  sendUpcomingMeetingReminderEmail: boolean
  sendWeeklyDigestEmail: boolean
  sendConsentNotification: boolean
  joinMeetingInternal: boolean
  joinMeetingExternal: boolean
  freezeJoinSettings: boolean
  useSectionHeadings: boolean
  usePartialMinutesRegeneration: boolean
  allowRestrictedUsers: boolean
  allowMeetingEntities: boolean
  allowLlmSpeechRecovery: boolean
  sessionTimeoutInMinutes: number
  dashboardUrl: string | null
  enabledMinutesLanguages: string[]
  minutingAgents: MinutingAgent[]
  useVirtualHostedStorageUrls: boolean
  contactEmails: string[]
  context: string[]
  assistantDisplayName: string | null
  llmModel: string | null
  encryptionKeySource: EncryptionKeySource
  retentionPeriodInDaysForMedia: number | null
  retentionPeriodInDaysForDocuments: number | null
  retentionPeriodInDaysForTranscript: number | null
  retentionPeriodInDaysForMeeting: number | null
  maxSingleRetentionExtensionInDays: number | null
  maxTotalRetentionExtensionInDays: number | null
  /** The contract the usage dashboard measures against; null until support records it. */
  subscriptionStartDate: string | null
  subscriptionAllowanceHours: number | null
  /** Percentages of the allowance at which the client's administrators are emailed. */
  usageAlertThresholdPercentages: number[]
  useAuthenticatedAssistant: boolean
  useNativeAssistant: boolean
}

/**
 * The effective configuration of a team: the client's settings, with the team's overrides of the
 * team settings applied and the merged settings unioned. Every setting a meeting reads is the
 * client's alone.
 */
export type ResolvedSettings = ClientSettings

/** The settings a team may override for its own people; a null there means "follow the client". */
export const TEAM_SETTING_NAMES = [
  'sessionTimeoutInMinutes',
  'isIntercomEnabled',
  'isCalendarSynchronizationEnabled',
  'sendWeeklyDigestEmail',
  'allowManualMeetingCreation',
  'allowMeetingUpload',
] as const

/** Combined rather than overridden: the client's entries plus the team's. */
export const MERGED_TEAM_SETTING_NAMES = ['contactEmails'] as const

export type TeamSettingName = (typeof TEAM_SETTING_NAMES)[number]
export type MergedTeamSettingName = (typeof MERGED_TEAM_SETTING_NAMES)[number]

export type TeamSettingOverrides = {
  [K in TeamSettingName]: ClientSettings[K] | null
}

type ResolvableTeam = {
  client: ClientSettings
} & Partial<Record<TeamSettingName, unknown>> &
  Partial<Record<MergedTeamSettingName, string[]>>

/**
 * Mirrors the backend's resolveSettings for the places that hold a raw team rather than a
 * payload carrying `resolvedSettings`.
 */
export function resolveSettings(team: ResolvableTeam): ResolvedSettings {
  const overrides = Object.fromEntries(
    TEAM_SETTING_NAMES.map((setting) => [setting, team[setting] ?? null] as const).filter(([, override]) => override !== null)
  )
  const merged = Object.fromEntries(MERGED_TEAM_SETTING_NAMES.map((setting) => [setting, [...new Set([...team.client[setting], ...(team[setting] ?? [])])]]))
  return { ...team.client, ...overrides, ...merged } as ResolvedSettings
}
